// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseInputSearch, BaseMoreAction, BaseTable, BaseToolbar, ConfirmModal, Loading } from '~/components/common';
import { EventTypeForm } from '~/components/module/setting';
// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_COUPON,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IPaginationResponse } from '~/utils/interface/base';
import { IMasterData, IQueryMasterData } from '~/utils/interface/masterData';
import { deleteMasterData, getListMasterData } from '~/thunks/masterData/masterDataThunk';
import { TypeMasterDataListEnum } from '~/utils/enum/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { masterDataActions } from '~/thunks/masterData/masterDataSlice';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { EVENT_TYPE_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './EventType.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsEventType = (
  t: TFunction,
  sortBy: ISortBy[],
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<IMasterData>[] => {
  // const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_title')}</Box>
        </Stack>
      ),
      render: (record: IMasterData) => <Box className={cx('textValue')}>{record?.name}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IMasterData) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const EventType = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateEventTypes, canViewEventTypes, canEditEventTypes, canDeleteEventTypes } =
    usePermissions(EVENT_TYPE_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/eventType', 'common/base']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isLoading, isRefreshMasterDataList } = useAppSelector((state) => state.masterData);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_COUPON);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isOpenEventTypeFormModal, setIsOpenEventTypeFormModal] = useState<boolean>(false);
  const [idEventType, setIdEventType] = useState<string>(EMPTY_STRING);
  const [eventTypeList, setEventTypeList] = useState<IMasterData[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListEventType(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshMasterDataList) return;

    handleGetListEventType(paramObject);

    dispatch(masterDataActions.setRefreshMasterDataList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshMasterDataList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListEventType = (params: IQueryParamBase) => {
    const paramsQuery: IQueryMasterData = {
      ...params,
      type: TypeMasterDataListEnum.EVENT,
    };

    dispatch(getListMasterData(paramsQuery))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setEventTypeList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleConfirmDelete = () => {
    dispatch(deleteMasterData(idEventType))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        handleCloseConfirmDelete();
      });
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleShowEventTypeFormModal = () => {
    setIsOpenEventTypeFormModal((prev) => !prev);
    setIdEventType(EMPTY_STRING);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleCloseConfirmDelete = () => {
    setIdEventType(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdEventType(id);
    setIsOpenEventTypeFormModal(true);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdEventType(id);
    setIsShowConfirmDelete(true);
  };
  //#endregion Handle Function

  return (
    <Box id='eventTypePage' className={cx('container')}>
      <BaseToolbar title={t('event_type_title')} isBack description={t('event_type_sub_title')}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateEventTypes && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowEventTypeFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('event_type_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewEventTypes ? eventTypeList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsEventType(t, sortBy, handleEdit, handleDelete, canEditEventTypes, canDeleteEventTypes)}
          onPageChange={handlePageChange}
          {...(canEditEventTypes ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isOpenEventTypeFormModal && <EventTypeForm idEventType={idEventType} onClose={handleShowEventTypeFormModal} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default EventType;
